import type { DerivedButton } from "@/types/look-system";

const NAME = /^[a-z0-9-]{1,40}$/;
const COLOUR = /^(?:#(?:[0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})|transparent)$/i;
const HEX = /^#([0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;

/** A palette key, a redirect or a global colour's role as the CMS stores it. */
export const isName = (value: unknown): value is string =>
  typeof value === "string" && NAME.test(value);

/** What a palette, derived or global colour may hold: hex or `transparent`. */
export const isColour = (value: unknown): value is string =>
  typeof value === "string" && COLOUR.test(value);

export const record = (value: unknown): Record<string, unknown> =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

/** A stored `{ bg, fg }`, only its two colours; undefined unless both are colours. */
export function colourPair(
  value: unknown,
): { bg: string; fg: string } | undefined {
  const { bg, fg } = record(value);
  return isColour(bg) && isColour(fg) ? { bg, fg } : undefined;
}

/** A stored derived button, only its colours; undefined unless every one of them is a colour. */
export function derivedButton(value: unknown): DerivedButton | undefined {
  const { bg, fg, border, hover, active } = record(value);
  const [hoverPair, activePair] = [colourPair(hover), colourPair(active)];
  return isColour(bg) &&
    isColour(fg) &&
    isColour(border) &&
    hoverPair &&
    activePair
    ? { bg, fg, border, hover: hoverPair, active: activePair }
    : undefined;
}

/** WCAG 2.2 relative luminance of 0–255 channels. */
export function channelLuminance({
  r,
  g,
  b,
}: {
  r: number;
  g: number;
  b: number;
}): number {
  const [lr, lg, lb] = [r, g, b].map((value) => {
    const channel = value / 255;
    return channel <= 0.04045
      ? channel / 12.92
      : ((channel + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * lr + 0.7152 * lg + 0.0722 * lb;
}

/** A hex colour's relative luminance, its alpha ignored; undefined for anything else. */
export function hexLuminance(colour: string): number | undefined {
  const digits = HEX.exec(colour)?.[1];
  if (!digits) return undefined;
  const full =
    digits.length <= 4
      ? [...digits].map((digit) => digit + digit).join("")
      : digits;
  const [r, g, b] = [0, 2, 4].map((at) => parseInt(full.slice(at, at + 2), 16));
  return channelLuminance({ r, g, b });
}

/** WCAG 2.2 contrast; 1 where either colour is not a plain hex, such as `transparent`. */
export function contrast(a: string, b: string): number {
  const [la, lb] = [hexLuminance(a), hexLuminance(b)];
  if (la === undefined || lb === undefined) return 1;
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}
